import { createId } from "../../app/runtimeContext";
import type {
  KnowledgeGraphNode,
  KnowledgeGraphRelationship,
  KnowledgeRelationshipType,
} from "./liveTypes";

export class KnowledgeGraphService {
  private readonly nodes = new Map<string, KnowledgeGraphNode>();
  private readonly relationships = new Map<
    string,
    KnowledgeGraphRelationship
  >();

  addNode(
    node: Omit<KnowledgeGraphNode, "id"> & { id?: string },
  ): KnowledgeGraphNode {
    const saved = { ...node, id: node.id ?? createId("kg_node") };
    this.nodes.set(saved.id, saved);
    return saved;
  }

  relate(
    sourceId: string,
    targetId: string,
    type: KnowledgeRelationshipType,
    confidence = 0.8,
    metadata: Record<string, unknown> = {},
  ): KnowledgeGraphRelationship {
    const relationship = {
      id: createId("kg_rel"),
      sourceId,
      targetId,
      type,
      confidence,
      metadata,
    };
    this.relationships.set(relationship.id, relationship);
    return relationship;
  }

  neighborhood(nodeId: string): KnowledgeGraphRelationship[] {
    return [...this.relationships.values()].filter(
      (item) => item.sourceId === nodeId || item.targetId === nodeId,
    );
  }

  traverseDependencies(nodeId: string): KnowledgeGraphRelationship[] {
    return this.neighborhood(nodeId).filter(
      (item) => item.type === "depends_on" || item.type === "blocks",
    );
  }

  impact(nodeId: string): KnowledgeGraphRelationship[] {
    return this.neighborhood(nodeId).filter(
      (item) =>
        item.type === "affects" ||
        item.type === "references" ||
        item.type === "generated_from",
    );
  }
}
